// Monolane browser client. Draws the game, reads touch / mouse / keyboard, and runs either
// offline practice (engine in the browser) or an online match (engine on the server).
import PartySocket from 'partysocket';
import { createEngine, CLASSES, CLS_KEYS, ITEMS, ITEM, SLOTS, MH, LX0, LX1, HATCH, WELL, WELL_R, SIGHT, LVL_XP,
  TAU, DT, hyp, clamp, dist, fmtT, fmtK, descOf, CONCEDE_AT } from '../shared/sim.js';

const css = getComputedStyle(document.documentElement);
const tok = (n, f) => (css.getPropertyValue(n).trim() || f);
const INK = tok('--ink', '#111111'), PAPER = tok('--paper', '#FFFFFF'), ACCENT = tok('--accent', '#1F3BFF'), MUTE = tok('--mute', '#6B6F80'), RULE = tok('--rule', '#DCDFE8'), WASH = tok('--wash', '#F3F4F8');
const DISPLAY = '"Dela Gothic One","Arial Black",sans-serif';
const MONO = '"Martian Mono",ui-monospace,Menlo,monospace';
const TICK = 1000 / 30;   // server snapshot interval

/* ---------- small storage helpers (wrapped: private windows can throw) ---------- */
const load = k => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };
const save = (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch {} };
const PID = load('ml.pid') || (() => { const p = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now()).replace(/[^\w-]/g, ''); save('ml.pid', p); return p; })();
const NAME = load('ml.name') || (() => { const n = 'Guest-' + String(1000 + Math.floor(Math.random() * 9000)); save('ml.name', n); return n; })();
let lastMode = [1, 2, 3].includes(load('ml.mode')) ? load('ml.mode') : 1;
let lastCls = CLASSES[load('ml.cls')] ? load('ml.cls') : 'dart';
let playMode = load('ml.play') === 'practice' ? 'practice' : 'online';

/* ---------- engine (offline practice) + queries that work on any view ---------- */
const PE = createEngine();
const { tierOf, cdOf, wDist, eRad, sellValue } = PE;
let G = null;   // what is on screen: the practice engine's state, or the online view built from snapshots
const isStruct = u => u.kind === 'tower' || u.kind === 'core';
const isMinion = u => u.kind === 'melee' || u.kind === 'ranged';
const alive = u => !!u && u.alive && !(u.kind === 'champ' && u.dead > 0);
function inHatch(u) { for (let i = 0; i < HATCH.length; i++) { const b = HATCH[i]; if (u.x > b.x0 && u.x < b.x1 && u.y > b.y0 && u.y < b.y1) return i; } return -1; }
// online, the server only sends what your team can see, so anything present and alive is visible
const visible = (team, u) => G.net ? alive(u) : PE.visible(team, u);
function targetable(u) { if (!alive(u)) return false; if (!isStruct(u)) return true; return u.tier === 0 || !(G.struct[u.team][u.tier - 1] || {}).alive; }
const validFor = (team, u) => !!u && u.team !== team && targetable(u) && visible(team, u);
const atWell = c => dist(c, WELL[c.team]) < WELL_R;
const canShop = c => c.dead > 0 || atWell(c);
function wellRate(team) {
  const s = G.struct[team];
  if (s[0] && s[1] && !s[0].alive && !s[1].alive) return 0;
  return G.t > 300 ? 0.015 : 0.14;
}
const edge = (a, b) => dist(a, b) - a.r - b.r;
const fcol = f => f.tm === -1 ? INK : f.tm == null ? (f.col || ACCENT) : f.tm === 0 ? ACCENT : INK;
function champName(c) {
  if (c === G.me) return 'YOU';
  const away = c.away ? ' · AWAY' : '';
  if (c.human && c.name) return c.name.toUpperCase().slice(0, 12) + away;
  if (G.mode === 1) return 'BOT';
  return (c.team === 0 ? 'ALLY BOT ' : 'BOT ') + (c.idx + 1);
}
// anything not on the real domain is the test site; the menu says so
const IS_TEST = !/(^|\.)playmonolane\.com$/.test(location.hostname);
const devType = () => (G && G.lastPointer === 'mouse' ? 'mouse' : 'touch');

/* ---------- canvas + drawing helpers ---------- */
/* ---------- canvas ---------- */
const cv = document.getElementById('game'), ctx = cv.getContext('2d');
const fogC = document.createElement('canvas'), fctx = fogC.getContext('2d');
let k = 1;
// The world is always 1000 units wide. VH is how many units tall the view is: 1250 on wide screens,
// up to 1800 on tall phones so they see more of the lane instead of empty space.
let VH = 1250;
const stage = document.getElementById('stage');
// inside another page (itch.io, a game portal): show only the game, no guide below and no page scrolling
const EMBED = window.self !== window.top || /[?&]from=itch\b/.test(location.search);
if (EMBED) document.body.classList.add('embed');
const isFull = () => !!(document.fullscreenElement || document.webkitFullscreenElement || stage.classList.contains('fs'));
function fit() {
  const full = isFull(), pad = full ? 0 : EMBED ? 3 : 12;
  const aw = Math.max(200, stage.clientWidth - pad * 2), ah = Math.max(250, stage.clientHeight - pad * 2);
  const ratio = clamp(ah / aw, 1.25, 1.8);
  const cssW = Math.floor(Math.min(aw, ah / ratio)), cssH = Math.floor(cssW * ratio);
  cv.style.width = cssW + 'px'; cv.style.height = cssH + 'px';
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.max(1, Math.round(cssW * dpr)), h = Math.round(w * cssH / cssW);
  if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; fogC.width = w; fogC.height = h; }
  k = w / 1000;
  VH = 1000 * h / w;
  if (typeof layoutButtons === 'function') layoutButtons();
  if (G) render();
}
if (window.ResizeObserver) new ResizeObserver(fit).observe(stage); else addEventListener('resize', fit);
addEventListener('orientationchange', () => setTimeout(fit, 250));

/* full screen: the real browser full screen where allowed (desktop, Android), otherwise a fixed
   overlay that fills the visible window (iPhone Safari only allows real full screen for video) */
function toggleFull() {
  if (isFull()) {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
    stage.classList.remove('fs'); document.body.classList.remove('nofloat');
  } else {
    const req = stage.requestFullscreen || stage.webkitRequestFullscreen;
    const fallback = () => { stage.classList.add('fs'); document.body.classList.add('nofloat'); fit(); };
    if (req) { try { const r = req.call(stage, { navigationUI: 'hide' }); if (r && r.catch) r.catch(fallback); } catch { fallback(); } }
    else fallback();
  }
  setTimeout(fit, 60);
}
document.addEventListener('fullscreenchange', () => { stage.classList.toggle('native', !!document.fullscreenElement); fit(); updateFsBtn(); });
document.addEventListener('webkitfullscreenchange', () => { fit(); updateFsBtn(); });
const fsBtn = document.getElementById('fsbtn');
function updateFsBtn() { if (fsBtn) fsBtn.textContent = isFull() ? 'Exit full screen' : 'Full screen'; }
if (fsBtn) fsBtn.addEventListener('click', () => { toggleFull(); setTimeout(updateFsBtn, 80); cv.focus({ preventScroll: true }); });

/* ---------- drawing helpers ---------- */
function txt(s, x, y, size, o = {}) {
  ctx.font = `${o.weight || 400} ${size}px ${o.font || MONO}`;
  ctx.fillStyle = o.color || INK; ctx.textAlign = o.align || 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(s, x, y);
}
function disc(x, y, r, fill) { ctx.beginPath(); ctx.arc(x, y, Math.max(0, r), 0, TAU); ctx.fillStyle = fill; ctx.fill(); }
function circ(x, y, r, stroke, w, dash) { ctx.beginPath(); ctx.setLineDash(dash || []); ctx.arc(x, y, Math.max(0, r), 0, TAU); ctx.strokeStyle = stroke; ctx.lineWidth = w; ctx.stroke(); ctx.setLineDash([]); }
function seg(x1, y1, x2, y2, stroke, w, dash) { ctx.beginPath(); ctx.setLineDash(dash || []); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.strokeStyle = stroke; ctx.lineWidth = w; ctx.lineCap = 'butt'; ctx.stroke(); ctx.setLineDash([]); }
function hatchRect(x0, y0, x1, y1, col, w, gap) {
  ctx.save(); ctx.beginPath(); ctx.rect(x0, y0, x1 - x0, y1 - y0); ctx.clip();
  const h = y1 - y0;
  for (let x = x0 - h; x < x1; x += gap) seg(x, y1, x + h, y0, col, w);
  ctx.restore();
}
function shapePath(kind, x, y, r, ang) {
  ctx.beginPath();
  if (kind === 'circ') ctx.arc(x, y, r, 0, TAU);
  else if (kind === 'sq') { const s = r * 0.88; ctx.rect(x - s, y - s, s * 2, s * 2); }
  else { const R = r * 1.25, a = ang == null ? -Math.PI / 2 : ang; for (let i = 0; i < 3; i++) { const t = a + i * TAU / 3; ctx.lineTo(x + Math.cos(t) * R, y + Math.sin(t) * R); } ctx.closePath(); }
}
function drawShape(kind, x, y, r, o) {
  shapePath(kind, x, y, r, o.ang);
  if (o.fill) { ctx.fillStyle = o.fill; ctx.fill(); }
  if (o.stroke) { ctx.lineJoin = 'miter'; ctx.strokeStyle = o.stroke; ctx.lineWidth = o.lw || 4; ctx.stroke(); }
}
function banner(s, y, size, color) {
  ctx.font = `400 ${size}px ${DISPLAY}`;
  const w = ctx.measureText(s).width + 60;
  ctx.fillStyle = PAPER; ctx.fillRect(500 - w / 2, y - size * 0.75, w, size * 1.5);
  ctx.strokeStyle = INK; ctx.lineWidth = 4; ctx.strokeRect(500 - w / 2, y - size * 0.75, w, size * 1.5);
  txt(s, 500, y + 2, size, { font: DISPLAY, color: color || INK });
}
/* ---------- screens: menu, practice, online ---------- */
function ui(g, phase, prev) {
  return Object.assign(g, {
    phase, cam: MH - VH + 280, joy: null, aim: null, held: null, endT: 0,
    lastPointer: prev ? prev.lastPointer : (matchMedia('(pointer: coarse)').matches ? 'touch' : null),
    shopMode: 'buy', shopHidden: false, wasShop: false, revealT: 2.4, pickMode: lastMode, concedeArm: -1e9, vote: null, toast: prev ? prev.toast : null
  });
}
function toMenu(phase = 'idle', toast) {
  MM.close();
  Online.leave();
  const prev = G;
  PE.newGame(lastMode, lastCls);
  G = ui(PE.G, phase, prev);
  G.menu = true;
  if (toast) G.toast = { s: toast, at: performance.now() };
}
function startPick(mode) { lastMode = mode; save('ml.mode', mode); G.phase = 'pick'; G.pickMode = mode; }
function lockIn(cls) {
  lastCls = cls; save('ml.cls', cls);
  if (playMode === 'practice') {
    const prev = G;
    PE.newGame(lastMode, cls);
    G = ui(PE.G, 'reveal', prev);
    G.practice = true;
  } else {
    G.phase = 'queue';
    MM.queue(lastMode, cls);
  }
}
function setPlayMode(m) { playMode = m; save('ml.play', m); }

// every player action goes through here: straight into the local engine for practice, over the socket online
function send(cmd) {
  if (G.net) Online.send(cmd);
  else if (G.practice && G.me) PE.command(G.me, cmd);
}

/* ---------- matchmaking ---------- */
const MM = {
  ws: null, info: null,
  queue(mode, cls) {
    this.close();
    this.info = { mode, at: performance.now(), n: 1, need: mode * 2, fillIn: 8000, recvAt: performance.now(), err: null };
    const ws = this.ws = new PartySocket({ host: location.host, party: 'matchmaker', room: 'global' });
    // re-sent on every reconnect, so a dropped connection quietly re-joins the queue
    ws.addEventListener('open', () => ws.send(JSON.stringify({ t: 'queue', mode, cls, pid: PID, name: NAME, device: devType() })));
    ws.addEventListener('message', e => {
      let m; try { m = JSON.parse(e.data); } catch { return; }
      if (m.t === 'queued') Object.assign(this.info, { n: m.n, need: m.need, fillIn: m.fillIn, recvAt: performance.now() });
      else if (m.t === 'match') { this.close(); Online.join(m.id); }
      else if (m.t === 'error') this.info.err = m.s;
    });
  },
  cancel() { if (this.ws) { try { this.ws.send(JSON.stringify({ t: 'cancel' })); } catch {} } this.close(); },
  close() { if (this.ws) { this.ws.close(); this.ws = null; } }
};

/* ---------- online match: socket + a view rebuilt from server snapshots ---------- */
const STRUCT_Y = [[2050, 2500, 2850], [950, 500, 150]];
const Online = {
  ws: null, id: null, open: false, flip: false, you: 0, recvAt: 0, serverT: 0, units: new Map(), champs: new Map(),
  join(id) {
    this.leave();
    this.id = id; this.units = new Map(); this.champs = new Map();
    save('ml.match', { id, at: Date.now() });
    const prev = G;
    G = ui({ net: true, mode: lastMode, t: 0, units: [], champs: [], struct: [[], []], projs: [], fields: [], fx: [], texts: [], kills: [0, 0], notice: null, waveN: 0, waveT: 4, over: null, me: null, connecting: true }, 'reveal', prev);
    const ws = this.ws = new PartySocket({ host: location.host, party: 'match', room: id, query: { pid: PID } });
    ws.addEventListener('open', () => { this.open = true; lastVec = ''; ws.send(JSON.stringify({ t: 'device', v: devType() })); });
    ws.addEventListener('close', () => { this.open = false; });
    ws.addEventListener('message', e => { let m; try { m = JSON.parse(e.data); } catch { return; } this.onMsg(m); });
  },
  leave() { if (this.ws) { this.ws.close(); this.ws = null; } this.open = false; this.id = null; },
  X(x) { return this.flip ? 1000 - x : x; },
  Y(y) { return this.flip ? MH - y : y; },
  T(t) { return t === -1 ? -1 : this.flip ? 1 - t : t; },
  send(m) {
    if (!this.ws || !this.open) return;
    const o = Object.assign({}, m);
    if (this.flip) {
      if (typeof o.x === 'number') o.x = 1000 - o.x;
      if (typeof o.y === 'number') o.y = MH - o.y;
      if (typeof o.dx === 'number') o.dx = -o.dx;
      if (typeof o.dy === 'number') o.dy = -o.dy;
    }
    this.ws.send(JSON.stringify(o));
  },
  sendRaw(o) { if (this.ws && this.open) this.ws.send(JSON.stringify(o)); },
  champ(id) {
    let c = this.champs.get(id);
    if (!c) { c = { id, kind: 'champ', r: 22, alive: true, x: 500, y: 1500, hp: 1, maxHp: 1, lvl: 1, dead: 0, face: { x: 0, y: -1 }, items: [], cd: { q: 0, w: 0, e: 0 }, st: { k: 0, d: 0, a: 0, cs: 0, dmgChamp: 0 }, gold: 0, xp: 0, cls: CLASSES.dart, idx: 0 }; this.champs.set(id, c); this.units.set(id, c); }
    return c;
  },
  setPos(u, x, y, now) {
    if (!u.seen || hyp(x - u.x, y - u.y) > 260) { u.x = u.fx = u.tx = x; u.y = u.fy = u.ty = y; }
    else { u.fx = u.x; u.fy = u.y; u.tx = x; u.ty = y; }
    u.lt = now; u.seen = true;
  },
  onMsg(m) {
    if (m.t === 'gone') { save('ml.match', null); toMenu('idle', 'That match is over.'); return; }
    if (m.t === 'replaced') { this.leave(); toMenu('idle', 'You opened this match somewhere else.'); return; }
    if (!G.net) return;
    if (m.t === 'hello') return this.hello(m);
    if (m.t === 's') return this.snap(m);
    if (m.t === 'vote') { G.vote = { yes: m.yes, need: m.need, until: performance.now() + m.until * 1000 }; return; }
    if (m.t === 'end') return this.end(m);
  },
  hello(m) {
    this.you = m.you; this.flip = m.team === 1;
    G.mode = m.mode; lastMode = m.mode; G.concedeAt = m.concedeAt; G.connecting = false;
    G.champs = m.roster.map(([id, team, idx, cls, name, human]) => {
      const c = this.champ(id);
      Object.assign(c, { team: this.T(team), idx, cls: CLASSES[cls] || CLASSES.dart, name, human: !!human });
      return c;
    });
    G.me = this.champ(m.you);
    if (m.phase === 'play' || m.phase === 'over') { if (G.phase === 'reveal') G.phase = 'play'; }
    else { G.revealT = Math.max(0.5, m.revealLeft / 1000); G.waiting = m.phase === 'wait'; }
  },
  snap(s) {
    const now = performance.now();
    this.recvAt = now; this.serverT = s.tm;
    G.t = s.tm; G.kills = this.flip ? [s.k[1], s.k[0]] : s.k; G.waveN = s.wn; G.waveT = s.wt;
    G.notice = s.n ? { s: s.n[0], t: s.n[1] } : null;
    G.waiting = s.ph === 'wait';
    if (s.ph === 'play' && G.phase === 'reveal') G.phase = 'play';
    for (const [id, team, tier, al, hp, maxHp, tgt] of s.S) {
      let u = this.units.get(id);
      if (!u) { u = { id, kind: tier === 2 ? 'core' : 'tower', tier, r: tier === 2 ? 56 : 38, range: 350, x: 500 }; this.units.set(id, u); }
      u.team = this.T(team); u.y = STRUCT_Y[u.team][tier]; u.alive = !!al; u.hp = hp; u.maxHp = maxHp; u.tgtId = tgt;
      G.struct[u.team][tier] = u;
    }
    for (const row of s.C) {
      const [id, team, lvl, dead, away, vis] = row, c = this.champ(id);
      c.team = this.T(team); c.lvl = lvl; c.dead = dead; c.away = !!away;
      if (vis) {
        const [x, y, hp, maxHp, ang, castT, braceR, slow, items] = row.slice(6);
        c.alive = true; this.setPos(c, this.X(x), this.Y(y), now);
        c.hp = hp; c.maxHp = maxHp;
        const a = this.flip ? ang + Math.PI : ang; c.face = { x: Math.cos(a), y: Math.sin(a) };
        c.cast = castT >= 0 ? { t: castT } : null; c.brace = braceR ? { r: braceR } : null; c.slowUntil = slow ? Infinity : 0;
        c.items = items ? items.split(',').map(s => ({ key: s.slice(0, -1), tier: +s.slice(-1) })) : [];
      } else { c.alive = c.team === 0; c.seen = false; }
    }
    const mins = [];
    for (const [id, team, k, x, y, hp, maxHp] of s.M) {
      let u = this.units.get(id);
      if (!u) { u = { id, kind: k === 1 ? 'melee' : 'ranged', r: k === 1 ? 14 : 13, alive: true }; this.units.set(id, u); }
      u.team = this.T(team); u.hp = hp; u.maxHp = maxHp; u.alive = true; u.live = now;
      this.setPos(u, this.X(x), this.Y(y), now);
      mins.push(u);
    }
    for (const [id, u] of this.units) if (isMinion(u) && u.live !== now) this.units.delete(id);
    const structs = [...G.struct[0], ...G.struct[1]].filter(Boolean);
    G.units = structs.concat(G.champs, mins);
    for (const st of structs) st.target = st.tgtId ? this.units.get(st.tgtId) || null : null;
    G.projs = s.P.map(([k, sub, team, x, y, vx, vy, size]) => ({ kind: k ? 'homing' : 'bolt', sub: sub === 'r' ? 'ring' : 'dart', team: this.T(team), bx: this.X(x), by: this.Y(y), x: this.X(x), y: this.Y(y), vx: this.flip ? -vx : vx, vy: this.flip ? -vy : vy, size }));
    G.fields = s.F.map(([x, y, r, team]) => ({ x: this.X(x), y: this.Y(y), r, team: this.T(team) }));
    if (!document.hidden) for (const f of s.X) {
      const o = Object.assign({}, f, { age: 0, tm: this.T(f.tm) });
      o.x = this.X(f.x); o.y = this.Y(f.y);
      if (this.flip && typeof o.ux === 'number') { o.ux = -o.ux; o.uy = -o.uy; }
      G.fx.push(o);
    }
    if (!document.hidden) for (const [x, y, str] of s.Y || []) G.texts.push({ x: this.X(x), y: this.Y(y), s: str, age: 0 });
    if (s.me) {
      const c = G.me, me = s.me;
      c.gold = me.gold; c.xp = me.xp; c.cd = { q: me.cd[0], w: me.cd[1], e: me.cd[2] }; c.dead = me.dead;
      c.items = me.items.map(([key, tier]) => ({ key, tier }));
      c.atk = me.atk ? this.units.get(me.atk) || null : null;
      const K = c.cls; c.ad = K.ad + K.adL * (c.lvl - 1) + [0, 18, 40][tierOf(c, 'blade')]; c.range = K.range;
    }
  },
  // between snapshots: glide units toward their latest position and advance shots along their velocity
  tick(now) {
    if (!G.net || !this.recvAt) return;
    const a = clamp((now - this.recvAt) / TICK, 0, 1), ahead = Math.min(0.05, (now - this.recvAt) / 1000);
    for (const u of G.units) if (u.lt) { u.x = u.fx + (u.tx - u.fx) * a; u.y = u.fy + (u.ty - u.fy) * a; }
    for (const p of G.projs) { p.x = p.bx + p.vx * ahead; p.y = p.by + p.vy * ahead; }
    if (G.phase === 'play') G.t = this.serverT + ahead;
  },
  end(m) {
    G.over = this.T(m.w); G.endReason = m.why; G.phase = 'over'; G.endT = 0;
    G.kills = this.flip ? [m.k[1], m.k[0]] : m.k;
    for (const [id, lvl, k, d, a, cs, dmg, items] of m.C) {
      const c = this.champ(id);
      c.lvl = lvl; c.st = { k, d, a, cs, dmgChamp: dmg }; c.left = m.left.includes(id);
      c.items = items ? items.split(',').map(s => ({ key: s.slice(0, -1), tier: +s.slice(-1) })) : [];
    }
    save('ml.match', null);
    this.leave();
  }
};

// conceding: two taps within 3 s. Online it is a team vote; in practice it ends the match.
function concedeTap() {
  const now = performance.now();
  if (now - G.concedeArm > 3000) { G.concedeArm = now; return; }
  G.concedeArm = -1e9;
  if (G.net) Online.sendRaw({ t: 'concede' });
  else if (G.practice) PE.concede(0);
}
const concedeOpen = () => G.phase === 'play' && (G.net || G.practice) && G.t >= (G.net && G.concedeAt != null ? G.concedeAt : CONCEDE_AT[G.mode]);

/* ---------- input ---------- */
const BTN = {
  atk: { x: 868, y: 1128, r: 82, label: 'ATK' },
  q: { x: 690, y: 1172, r: 58, label: 'Q' },
  w: { x: 718, y: 1012, r: 58, label: 'W' },
  e: { x: 872, y: 940, r: 58, label: 'E' }
};
// the ability buttons sit at a fixed distance from the bottom edge, whatever the view height
const menuOff = () => Math.max(0, (VH - 1250) / 2);
function layoutButtons() { BTN.atk.y = VH - 122; BTN.q.y = VH - 78; BTN.w.y = VH - 238; BTN.e.y = VH - 310; FS_BTN.y = 262; }
const FS_BTN = { x: 772, y: 262, w: 146, h: 40 };
const PANEL = { x: 40, y: 146, w: 900, h: 660 };
const TABS = [
  { id: 'buy', label: 'BUY · UPGRADE', x: 52, y: 158, w: 290, h: 56 },
  { id: 'sell', label: 'SELL · 50% BACK', x: 352, y: 158, w: 290, h: 56 },
  { id: 'hide', label: 'HIDE', x: 828, y: 158, w: 100, h: 56 }
];
const SHOP_BTN = { x: 420, y: 102, w: 112, h: 34 };
const BACK_BTN = { x: 90, y: 965, w: 240, h: 60 };
const tileRects = () => ITEMS.map((s, i) => ({ s, x: 52 + (i % 2) * 446, y: 228 + Math.floor(i / 2) * 142, w: 434, h: 130 }));
const modeRects = () => [1, 2, 3].map((n, i) => ({ n, x: 500 - 375 + i * 255, y: 650, w: 235, h: 120 }));
const playRects = () => [{ id: 'online', label: 'ONLINE', x: 125, y: 520, w: 365, h: 64 }, { id: 'practice', label: 'PRACTICE · OFFLINE', x: 510, y: 520, w: 365, h: 64 }];
const CONCEDE_BTN = { x: 560, y: 102, w: 150, h: 34 };
const classRects = () => CLS_KEYS.map((key, i) => ({ key, x: 90, y: 290 + i * 215, w: 820, h: 200 }));
function screenPt(e) { const r = cv.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width * 1000, y: (e.clientY - r.top) / r.height * VH }; }
const toWorld = p => ({ x: p.x, y: p.y + G.cam });
const inRect = (p, r) => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
function hitBtn(p) { for (const key in BTN) { const b = BTN[key]; if (hyp(p.x - b.x, p.y - b.y) <= b.r + 8) return key; } return null; }
function shopHit(p) {
  if (G.phase !== 'play' || !canShop(G.me)) return null;
  if (G.shopHidden) return inRect(p, SHOP_BTN) ? { type: 'show' } : null;
  for (const t of TABS) if (inRect(p, t)) return { type: 'tab', id: t.id };
  for (const r of tileRects()) if (inRect(p, r)) return { type: 'item', key: r.s.key };
  return null;
}
function shopTap(key) {
  if (G.shopMode === 'sell') { if (tierOf(G.me, key)) { send({ t: 'sell', key }); G.shopMode = 'buy'; } }
  else send({ t: 'buy', key });
}
function enemyAt(w) {
  let best = null, bd = Infinity;
  for (const u of G.units) { if (!validFor(0, u)) continue; const d = dist(u, w) - u.r; if (d <= 22 && d < bd) { bd = d; best = u; } }
  return best;
}
function fire(key, aimVec, auto) {
  const c = G.me;
  if (!c || c.dead > 0 || G.phase !== 'play') return;
  if (key === 'atk' || key === 'e') { send({ t: key }); return; }
  let p = null;
  if (aimVec) p = { x: c.x + aimVec.x * 600, y: c.y + aimVec.y * 600 };
  else if (!auto && mouseWorld() && G.lastPointer === 'mouse') p = mouseWorld();
  send(p ? { t: key, x: p.x, y: p.y } : { t: key });   // no point: the engine auto-aims
}
/* desktop movement: WASD / arrow keys steer like the phone stick */
const keysDown = new Set();
const MOVE_KEYS = { KeyW: [0, -1], ArrowUp: [0, -1], KeyS: [0, 1], ArrowDown: [0, 1], KeyA: [-1, 0], ArrowLeft: [-1, 0], KeyD: [1, 0], ArrowRight: [1, 0] };
function keyVec() {
  let x = 0, y = 0;
  for (const k of keysDown) { const v = MOVE_KEYS[k]; if (v) { x += v[0]; y += v[1]; } }
  x = clamp(x, -1, 1); y = clamp(y, -1, 1);
  if (!x && !y) return null;
  const L = hyp(x, y); return { dx: x / L * 90, dy: y / L * 90 };
}
window.addEventListener('keyup', e => keysDown.delete(e.code));
window.addEventListener('blur', () => keysDown.clear());

/* desktop cursor: high-contrast reticle (ink on a white halo), blue attack reticle over enemies */
const svgCur = (body, hot) => `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>${body}</svg>`)}") ${hot} ${hot}, crosshair`;
const RET = (col) => `<g fill='none' stroke-linecap='square'><circle cx='16' cy='16' r='9' stroke='#fff' stroke-width='6'/><path d='M16 1v9M16 22v9M1 16h9M22 16h9' stroke='#fff' stroke-width='6'/><circle cx='16' cy='16' r='9' stroke='${col}' stroke-width='2.5'/><path d='M16 2v8M16 22v8M2 16h8M22 16h8' stroke='${col}' stroke-width='2.5'/></g><circle cx='16' cy='16' r='2.5' fill='${col}' stroke='#fff' stroke-width='1.5'/>`;
const CUR_MOVE = svgCur(RET(INK), 16);
const CUR_ATK = svgCur(RET(ACCENT) + `<circle cx='16' cy='16' r='13.5' fill='none' stroke='${ACCENT}' stroke-width='2'/>`, 16);
let curNow = '';
function setCursor(c) { if (c !== curNow) { cv.style.cursor = c; curNow = c; } }
function updateCursor(p) {
  if (!G || G.phase !== 'play') return setCursor(G && ['idle', 'pick', 'queue', 'over'].includes(G.phase) ? 'pointer' : 'default');
  if (hitBtn(p) || shopHit(p) || (concedeOpen() && inRect(p, CONCEDE_BTN))) return setCursor('pointer');
  setCursor(enemyAt(toWorld(p)) ? CUR_ATK : CUR_MOVE);
}

/* desktop aiming: hold Q to aim at the cursor, release to fire (or left-click). Right-click / Esc cancels. */
let mouseScr = null;
const mouseWorld = () => mouseScr ? toWorld(mouseScr) : null;
function holdAim(key, by) { if (G.me && !(G.me.dead > 0)) G.held = { key, by }; }
function releaseAim(fireIt) { const h = G.held; G.held = null; if (h && fireIt) fire(h.key, null); }
window.addEventListener('blur', () => { if (G) G.held = null; });
const pointers = new Map();
cv.addEventListener('contextmenu', e => e.preventDefault());
cv.addEventListener('pointerdown', e => {
  e.preventDefault();
  cv.focus({ preventScroll: true });
  G.lastPointer = e.pointerType;
  const p = screenPt(e);
  if (G.phase === 'idle' || G.phase === 'pick' || G.phase === 'queue') p.y -= menuOff();
  if (G.phase === 'idle') {
    G.toast = null;
    if (inRect(p, FS_BTN)) { toggleFull(); return; }
    const m = modeRects().find(r => inRect(p, r)); if (m) { startPick(m.n); return; }
    const t = playRects().find(r => inRect(p, r)); if (t) setPlayMode(t.id);
    return;
  }
  if (G.phase === 'pick') { const c = classRects().find(r => inRect(p, r)); if (c) lockIn(c.key); else if (inRect(p, BACK_BTN)) G.phase = 'idle'; return; }
  if (G.phase === 'queue') { if (inRect(p, BACK_BTN)) { MM.cancel(); G.phase = 'idle'; } return; }
  if (G.phase === 'reveal') return;
  if (G.phase === 'over') { if (G.endT > 1) toMenu(); return; }
  if (concedeOpen() && inRect(p, CONCEDE_BTN)) { concedeTap(); return; }
  try { cv.setPointerCapture(e.pointerId); } catch (_) {}
  if (e.pointerType === 'mouse') mouseScr = p;
  if (G.held) {
    if (e.button === 2) { G.held = null; return; }          // right-click cancels
    if (!hitBtn(p) && !shopHit(p)) { releaseAim(true); return; } // left-click on the field fires
    G.held = null;                                           // clicking a button cancels, then handles that button
  }
  const sh = shopHit(p);
  if (sh) {
    if (sh.type === 'show') G.shopHidden = false;
    else if (sh.type === 'tab') { if (sh.id === 'hide') G.shopHidden = true; else G.shopMode = sh.id; }
    else shopTap(sh.key);
    return;
  }
  const b = hitBtn(p);
  if (b) {
    if (e.pointerType === 'mouse') { if (b === 'q' || b === 'w') holdAim(b, 'click'); else fire(b, null, true); return; }
    pointers.set(e.pointerId, { type: 'btn', key: b, x0: p.x, y0: p.y, x: p.x, y: p.y });
    if (b === 'atk' || b === 'b') fire(b, null);
    return;
  }
  const w = toWorld(p);
  if (e.pointerType !== 'mouse' && p.x < 540 && p.y > VH - 650 && !enemyAt(w)) {
    G.joy = { ax: p.x, ay: p.y, dx: 0, dy: 0 };
    pointers.set(e.pointerId, { type: 'joy' });
    return;
  }
  const t = enemyAt(w);
  if (!G.me || G.me.dead > 0) return;
  if (t) { send({ t: 'atk', id: t.id }); G.fx.push({ kind: 'tgt', u: t, age: 0, life: 0.35 }); }
  else {
    const x = clamp(w.x, LX0 + G.me.r, LX1 - G.me.r), y = clamp(w.y, 20, MH - 20);
    send({ t: 'go', x, y }); G.fx.push({ kind: 'mv', x, y, age: 0, life: 0.55 });
  }
});
cv.addEventListener('pointermove', e => {
  const p = screenPt(e);
  if (e.pointerType === 'mouse') { mouseScr = p; G.lastPointer = 'mouse'; updateCursor(p); }
  const ptr = pointers.get(e.pointerId);
  if (!ptr) return;
  if (ptr.type === 'joy' && G.joy) {
    let dx = p.x - G.joy.ax, dy = p.y - G.joy.ay; const L = hyp(dx, dy);
    if (L > 90) { dx = dx / L * 90; dy = dy / L * 90; }
    G.joy.dx = dx; G.joy.dy = dy;
  } else if (ptr.type === 'btn') {
    ptr.x = p.x; ptr.y = p.y;
    const dx = p.x - ptr.x0, dy = p.y - ptr.y0, L = hyp(dx, dy);
    G.aim = (ptr.key === 'q' || ptr.key === 'w') && L > 30 ? { key: ptr.key, x: dx / L, y: dy / L } : null;
  }
});
function endPtr(e) {
  const ptr = pointers.get(e.pointerId);
  if (!ptr) return;
  pointers.delete(e.pointerId);
  if (ptr.type === 'joy') { G.joy = null; return; }
  if (ptr.type === 'btn' && ptr.key !== 'atk' && ptr.key !== 'b' && e.type !== 'pointercancel') {
    const dx = ptr.x - ptr.x0, dy = ptr.y - ptr.y0, L = hyp(dx, dy);
    fire(ptr.key, L > 30 ? { x: dx / L, y: dy / L } : null);
  }
  G.aim = null;
}
cv.addEventListener('pointerup', endPtr); cv.addEventListener('pointercancel', endPtr);
cv.addEventListener('keydown', e => {
  const digit = { Digit1: 1, Digit2: 2, Digit3: 3 }[e.code];
  if (G.phase === 'idle') { if (digit) { e.preventDefault(); startPick(digit); } return; }
  if (G.phase === 'pick') { if (digit) { e.preventDefault(); lockIn(CLS_KEYS[digit - 1]); } else if (e.code === 'Escape') G.phase = 'idle'; return; }
  if (G.phase === 'queue') { if (e.code === 'Escape') { MM.cancel(); G.phase = 'idle'; } return; }
  if (G.phase === 'reveal') return;
  if (G.phase === 'over') { if ((e.code === 'Space' || e.code === 'Enter') && G.endT > 1) { e.preventDefault(); toMenu(); } return; }
  if (MOVE_KEYS[e.code]) { e.preventDefault(); G.lastPointer = 'mouse'; keysDown.add(e.code); return; }
  const map = { KeyQ: 'q', KeyE: 'e', Space: 'w', KeyF: 'atk' };
  if (map[e.code]) {
    e.preventDefault(); if (e.repeat) return;
    G.lastPointer = 'mouse';
    const k = map[e.code];
    if (k === 'q' && mouseScr) holdAim(k, 'key');
    else fire(k, null);
  }
  else if (e.code === 'Escape' && G.held) G.held = null;
  else if (e.code === 'KeyX' && canShop(G.me)) { G.shopHidden = false; G.shopMode = G.shopMode === 'sell' ? 'buy' : 'sell'; }
  else if (/^Digit[1-8]$/.test(e.code) && canShop(G.me)) shopTap(ITEMS[+e.code.slice(-1) - 1].key);
});
cv.addEventListener('keyup', e => {
  const k = { KeyQ: 'q' }[e.code];
  if (k && G.held && G.held.key === k && G.held.by === 'key') { e.preventDefault(); releaseAim(G.phase === 'play'); }
});
/* ---------- render ---------- */
/* ---------- render ---------- */
function hpBar(u, x, y, w, h) {
  ctx.fillStyle = PAPER; ctx.fillRect(x - w / 2 - 2, y - 2, w + 4, h + 4);
  ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.strokeRect(x - w / 2 - 2, y - 2, w + 4, h + 4);
  ctx.fillStyle = u.team === 0 ? ACCENT : INK; ctx.fillRect(x - w / 2, y, w * clamp(u.hp / u.maxHp, 0, 1), h);
}
function drawWorld() {
  const cam = G.cam;
  ctx.setTransform(k, 0, 0, k, 0, -cam * k);
  ctx.fillStyle = PAPER; ctx.fillRect(0, cam, 1000, VH);
  if (cam + VH > MH) hatchRect(0, MH, 1000, cam + VH, RULE, 3, 22);
  if (cam < 0) hatchRect(0, cam, 1000, 0, RULE, 3, 22);
  ctx.save(); ctx.beginPath(); ctx.rect(0, cam, LX0, VH); ctx.rect(LX1, cam, 1000 - LX1, VH); ctx.clip();
  for (let x = -VH; x < 1000; x += 22) seg(x, cam + VH, x + VH, cam, RULE, 3);
  ctx.restore();
  const vy0 = Math.max(0, cam), vy1 = Math.min(MH, cam + VH);
  seg(LX0, vy0, LX0, vy1, INK, 4); seg(LX1, vy0, LX1, vy1, INK, 4);
  seg(500, vy0, 500, vy1, RULE, 3, [16, 18]);
  seg(LX0, 1500, LX1, 1500, RULE, 3, [6, 10]);
  for (const b of HATCH) { hatchRect(b.x0, b.y0, b.x1, b.y1, INK, 2, 16); ctx.setLineDash([6, 6]); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.strokeRect(b.x0, b.y0, b.x1 - b.x0, b.y1 - b.y0); ctx.setLineDash([]); }
  WELL.forEach((f, i) => {
    const r = wellRate(i), col = i ? INK : ACCENT;
    circ(f.x, f.y, WELL_R, col, r >= 0.1 ? 3 : 2, r >= 0.1 ? [10, 8] : [3, 10]);
    if (r < 0.1) txt(r > 0 ? 'WELL LOW' : 'WELL DRY', f.x, f.y + (i ? WELL_R + 26 : -WELL_R - 26), 20, { weight: 600, color: col });
  });
  for (const f of G.fields) {
    const col = f.team ? INK : ACCENT;
    ctx.globalAlpha = 0.1; disc(f.x, f.y, f.r, col); ctx.globalAlpha = 1;
    circ(f.x, f.y, f.r, col, 4, [10, 8]);
  }
  for (const s of G.units) {
    if (!isStruct(s)) continue;
    const mine = s.team === 0;
    if (!s.alive) { const h = s.r; seg(s.x - h, s.y - h, s.x + h, s.y + h, RULE, 6); seg(s.x - h, s.y + h, s.x + h, s.y - h, RULE, 6); continue; }
    if (s.kind === 'tower' && !mine && alive(G.me) && dist(G.me, s) < s.range + 220) circ(s.x, s.y, s.range, s.target === G.me ? INK : MUTE, s.target === G.me ? 4 : 2, [10, 10]);
    ctx.save(); ctx.translate(s.x, s.y);
    let hw, hh;
    if (s.kind === 'core') { ctx.rotate(Math.PI / 4); hw = hh = s.r; } else { hw = 30; hh = 44; }
    const scol = mine ? ACCENT : INK;
    ctx.fillStyle = PAPER; ctx.fillRect(-hw, -hh, hw * 2, hh * 2);
    ctx.save(); ctx.beginPath(); ctx.rect(-hw, -hh, hw * 2, hh * 2); ctx.clip();
    for (let x = -hw - hh * 2; x < hw; x += 13) seg(x, hh, x + hh * 2, -hh, scol, 3);
    ctx.restore();
    ctx.strokeStyle = scol; ctx.lineWidth = 7; ctx.strokeRect(-hw + 3.5, -hh + 3.5, hw * 2 - 7, hh * 2 - 7);
    if (!targetable(s)) { ctx.beginPath(); ctx.arc(0, 0, Math.min(hw, hh) * 0.42, 0, TAU); ctx.fillStyle = PAPER; ctx.fill(); ctx.strokeStyle = scol; ctx.lineWidth = 4; ctx.stroke(); }
    ctx.restore();
    hpBar(s, s.x, s.y - (s.kind === 'core' ? s.r + 34 : 66), 110, 12);
  }
}
function drawFog() {
  fctx.setTransform(1, 0, 0, 1, 0, 0);
  fctx.clearRect(0, 0, fogC.width, fogC.height);
  fctx.globalCompositeOperation = 'source-over';
  fctx.fillStyle = RULE; fctx.globalAlpha = 0.7; fctx.fillRect(0, 0, fogC.width, fogC.height); fctx.globalAlpha = 1;
  fctx.globalCompositeOperation = 'destination-out';
  for (const a of G.units) {
    if (a.team !== 0 || !alive(a)) continue;
    const r = SIGHT[a.kind] * k, sx = a.x * k, sy = (a.y - G.cam) * k;
    if (sy < -r || sy > fogC.height + r) continue;
    const gr = fctx.createRadialGradient(sx, sy, r * 0.82, sx, sy, r);
    gr.addColorStop(0, 'rgba(0,0,0,1)'); gr.addColorStop(1, 'rgba(0,0,0,0)');
    fctx.fillStyle = gr; fctx.beginPath(); fctx.arc(sx, sy, r, 0, TAU); fctx.fill();
  }
  fctx.globalCompositeOperation = 'source-over';
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.drawImage(fogC, 0, 0);
  ctx.setTransform(k, 0, 0, k, 0, -G.cam * k);
}
function drawChamp(c, me) {
  const col = c.team ? INK : ACCENT, ang = Math.atan2(c.face.y, c.face.x), sh = c.cls.shape;
  if (c.cast) circ(c.x, c.y, eRad(c) * (1 - c.cast.t / 0.3), col, 4, [8, 8]);
  if (c.brace) { circ(c.x, c.y, c.brace.r, col, 3, [4, 8]); }
  drawShape(sh, c.x, c.y, c.r + 5, { fill: PAPER, ang });
  if (c.team === 0 && c !== me) drawShape(sh, c.x, c.y, c.r - 3, { fill: PAPER, stroke: ACCENT, lw: 7, ang });
  else drawShape(sh, c.x, c.y, c.r, { fill: col, ang });
  if (sh !== 'tri') seg(c.x + c.face.x * c.r * 0.9, c.y + c.face.y * c.r * 0.9, c.x + c.face.x * (c.r + 13), c.y + c.face.y * (c.r + 13), col, 5);
  if (inHatch(c) >= 0) circ(c.x, c.y, c.r + 12, col, 2, [3, 5]);
  if (G.t < c.slowUntil) txt('SLOW', c.x, c.y + c.r + 22, 15, { weight: 600, color: col });
  hpBar(c, c.x, c.y - c.r - 32, 76, 9);
  txt(String(c.lvl), c.x - 50, c.y - c.r - 27, 18, { weight: 600, color: col });
}
function drawUnits() {
  const me = G.me;
  for (const u of G.units) {
    if (!isMinion(u) || !alive(u) || !visible(0, u)) continue;
    const col = u.team === 0 ? ACCENT : INK;
    disc(u.x, u.y, u.r, col);
    if (u.kind === 'ranged') disc(u.x, u.y, u.r - 5, PAPER);
    if (u.hp < u.maxHp) hpBar(u, u.x, u.y - u.r - 14, 40, 5);
    if (u.team !== 0 && alive(me) && u.hp <= me.ad && edge(me, u) < me.range + 200) circ(u.x, u.y, u.r + 7, ACCENT, 2, [3, 4]);
  }
  for (const c of G.champs) if (alive(c) && visible(0, c)) drawChamp(c, me);
  if (alive(me) && me.atk) circ(me.atk.x, me.atk.y, me.atk.r + 10, ACCENT, 3);
  for (const p of G.projs) {
    const col = p.team ? INK : ACCENT;
    if (p.kind === 'bolt') {
      const L = hyp(p.vx, p.vy), ux = p.vx / L, uy = p.vy / L;
      if (p.sub === 'dart') seg(p.x - ux * 50, p.y - uy * 50, p.x, p.y, col, 10);
      else { circ(p.x, p.y, 13, col, 5); seg(p.x - ux * 40, p.y - uy * 40, p.x - ux * 16, p.y - uy * 16, col, 4, [6, 5]); }
      continue;
    }
    if (p.team !== 0 && p.src && !visible(0, p.src) && !visible(0, p.tgt)) continue;
    disc(p.x, p.y, p.size, col);
  }
  for (const f of G.fx) {
    const a = 1 - f.age / f.life;
    ctx.globalAlpha = a;
    if (f.kind === 'ring') circ(f.x, f.y, f.r0 + (f.r1 - f.r0) * (1 - a), fcol(f), f.w || 4);
    else if (f.kind === 'mv') { const s = 1 - f.age / f.life; circ(f.x, f.y, 10 + 30 * s, ACCENT, 5); seg(f.x - 9, f.y - 9, f.x + 9, f.y + 9, ACCENT, 5); seg(f.x - 9, f.y + 9, f.x + 9, f.y - 9, ACCENT, 5); }
    else if (f.kind === 'tgt') { if (alive(f.u)) circ(f.u.x, f.u.y, f.u.r + 8 + 16 * (1 - f.age / f.life), ACCENT, 5); }
    else if (f.kind === 'x') { const xc = fcol(f); seg(f.x - 12, f.y - 12, f.x + 12, f.y + 12, xc, 4); seg(f.x - 12, f.y + 12, f.x + 12, f.y - 12, xc, 4); }
    else if (f.kind === 'shove') seg(f.x + f.ux * 20, f.y + f.uy * 20, f.x + f.ux * f.len, f.y + f.uy * f.len, fcol(f), 26 * a);
    else if (f.kind === 'slash') seg(f.x - 18, f.y + 18, f.x + 18, f.y - 18, fcol(f), 6);
    ctx.globalAlpha = 1;
  }
  for (const t of G.texts) { ctx.globalAlpha = 1 - t.age / 0.9; txt(t.s, t.x, t.y - t.age * 50, 24, { weight: 600, color: t.col || ACCENT }); ctx.globalAlpha = 1; }
  if (G.aim && alive(me)) { const L = G.aim.key === 'q' ? me.cls.qRange : wDist(me); seg(me.x, me.y, me.x + G.aim.x * L, me.y + G.aim.y * L, ACCENT, 6, [12, 8]); }
  const mw = mouseWorld();
  if (G.held && mw && alive(me)) {
    let dx = mw.x - me.x, dy = mw.y - me.y; const D = hyp(dx, dy) || 1; dx /= D; dy /= D;
    const L = G.held.key === 'q' ? me.cls.qRange : wDist(me);
    seg(me.x, me.y, me.x + dx * L, me.y + dy * L, ACCENT, 6, [12, 8]);
    ctx.strokeStyle = ACCENT; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(me.x + dx * L, me.y + dy * L, 14, 0, Math.PI * 2); ctx.stroke();
    txt(G.held.by === 'key' ? 'RELEASE TO FIRE' : 'CLICK TO FIRE · RIGHT-CLICK CANCEL', me.x, me.y + me.r + 34, 16, { weight: 600, color: ACCENT });
  }
}
function drawShop(c) {
  ctx.fillStyle = PAPER; ctx.globalAlpha = 0.97; ctx.fillRect(PANEL.x, PANEL.y, PANEL.w, PANEL.h); ctx.globalAlpha = 1;
  ctx.strokeStyle = INK; ctx.lineWidth = 4; ctx.strokeRect(PANEL.x, PANEL.y, PANEL.w, PANEL.h);
  for (const t of TABS) {
    const on = t.id === G.shopMode;
    ctx.fillStyle = on ? INK : PAPER; ctx.fillRect(t.x, t.y, t.w, t.h);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(t.x, t.y, t.w, t.h);
    txt(t.label, t.x + t.w / 2, t.y + t.h / 2 + 1, 19, { weight: 600, color: on ? PAPER : INK });
  }
  txt(c.items.length + '/' + SLOTS + ' · ' + Math.floor(c.gold) + ' INK', 735, 187, 19, { weight: 600, color: ACCENT });
  const selling = G.shopMode === 'sell';
  for (const r of tileRects()) {
    const it = r.s, tier = tierOf(c, it.key);
    let bg = PAPER, border = ACCENT, title = it.label, desc = descOf(it, 1, c), price = String(it.cost), sub = '', fg = INK, hd = ACCENT, pc = ACCENT, dim = false, inset = false;
    if (!selling) {
      if (tier === 0) {
        const ok = c.items.length < SLOTS && c.gold >= it.cost;
        if (!ok) { bg = WASH; border = RULE; fg = MUTE; hd = MUTE; pc = MUTE; }
        if (c.items.length >= SLOTS) sub = 'SLOTS FULL · SELL ONE FIRST';
      } else {
        bg = ACCENT; border = ACCENT; fg = PAPER; hd = PAPER; pc = PAPER;
        if (tier === 1) { title = it.label + ' I'; price = 'UP ' + it.up; sub = 'NEXT: ' + descOf(it, 2, c); dim = c.gold < it.up; }
        else { title = it.label + ' II'; desc = descOf(it, 2, c); price = 'MAX'; inset = true; }
      }
    } else {
      if (tier === 0) { bg = WASH; border = RULE; fg = MUTE; hd = MUTE; pc = MUTE; price = ''; }
      else { border = INK; hd = INK; title = it.label + (tier === 2 ? ' II' : ' I'); desc = descOf(it, tier, c); price = 'SELL +' + sellValue({ key: it.key, tier }); }
    }
    ctx.fillStyle = bg; ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = border; ctx.lineWidth = 4; ctx.strokeRect(r.x + 2, r.y + 2, r.w - 4, r.h - 4);
    if (inset) { ctx.strokeStyle = PAPER; ctx.lineWidth = 3; ctx.strokeRect(r.x + 10, r.y + 10, r.w - 20, r.h - 20); }
    txt(title, r.x + 22, r.y + 38, 24, { weight: 600, color: hd, align: 'left' });
    if (price) { if (dim) ctx.globalAlpha = 0.55; txt(price, r.x + r.w - 22, r.y + 38, 21, { weight: 600, color: pc, align: 'right' }); ctx.globalAlpha = 1; }
    txt(desc, r.x + 22, r.y + 78, 19, { color: fg, align: 'left' });
    if (sub) { ctx.globalAlpha = 0.85; txt(sub, r.x + 22, r.y + 108, 16, { color: fg, align: 'left' }); ctx.globalAlpha = 1; }
  }
}
function drawHUD() {
  ctx.setTransform(k, 0, 0, k, 0, 0);
  const y = G.me;
  ctx.fillStyle = PAPER; ctx.fillRect(0, 0, 1000, 92); seg(0, 92, 1000, 92, INK, 3);
  txt('YOU · ' + y.cls.label + ' · LV ' + y.lvl, 24, 24, 21, { weight: 600, color: ACCENT, align: 'left' });
  ctx.fillStyle = RULE; ctx.fillRect(24, 44, 280, 16); ctx.fillStyle = ACCENT; ctx.fillRect(24, 44, 280 * clamp(y.hp / y.maxHp, 0, 1), 16);
  const nx = LVL_XP[y.lvl], px = LVL_XP[y.lvl - 1];
  ctx.fillStyle = RULE; ctx.fillRect(24, 66, 280, 6); ctx.fillStyle = INK; ctx.fillRect(24, 66, 280 * (nx == null ? 1 : clamp((y.xp - px) / (nx - px), 0, 1)), 6);
  txt(Math.ceil(Math.max(0, y.hp)) + '/' + y.maxHp, 314, 50, 15, { align: 'left', color: MUTE });
  txt(Math.floor(y.gold) + ' INK', 314, 74, 17, { align: 'left', weight: 600, color: ACCENT });
  txt(fmtT(G.t), 560, 28, 26, { weight: 600 });
  txt(G.kills[0] + ' – ' + G.kills[1], 560, 62, 20, { color: MUTE });
  const foes = G.champs.filter(c => c.team === 1), n = foes.length, rowH = 66 / n, bh = n === 1 ? 16 : n === 2 ? 12 : 9;
  foes.forEach((f, i) => {
    const vis = validFor(0, f), ry = 14 + i * rowH;
    if (n === 1) txt(champName(f) + ' · ' + f.cls.label + ' · LV ' + f.lvl, 976, 24, 20, { weight: 600, align: 'right', color: vis ? INK : MUTE });
    else { drawShape(f.cls.shape, 690, ry + rowH / 2, 8, { fill: vis ? INK : MUTE }); txt((f.dead > 0 ? '✕ ' : '') + 'LV ' + f.lvl, 760, ry + rowH / 2, 15, { weight: 600, align: 'right', color: vis ? INK : MUTE }); }
    const bx = n === 1 ? 696 : 770, bw = n === 1 ? 280 : 206, by = n === 1 ? 44 : ry + rowH / 2 - bh / 2;
    ctx.fillStyle = RULE; ctx.fillRect(bx, by, bw, bh);
    if (vis) { ctx.fillStyle = INK; ctx.fillRect(bx, by, bw * clamp(f.hp / f.maxHp, 0, 1), bh); }
    else if (n === 1) txt(f.dead > 0 ? 'RESPAWNING' : 'NOT VISIBLE', 836, 53, 13, { color: MUTE });
  });
  for (let i = 0; i < SLOTS; i++) {
    const x = 24 + i * 98, yy = 102, e = y.items[i];
    if (e) {
      if (e.tier === 2) { ctx.fillStyle = ACCENT; ctx.fillRect(x, yy, 90, 34); }
      else { ctx.fillStyle = PAPER; ctx.fillRect(x, yy, 90, 34); ctx.strokeStyle = ACCENT; ctx.lineWidth = 3; ctx.strokeRect(x + 1.5, yy + 1.5, 87, 31); }
      txt(ITEM[e.key].label + (e.tier === 2 ? ' II' : ''), x + 45, yy + 18, 13, { weight: 600, color: e.tier === 2 ? PAPER : ACCENT });
    } else { ctx.setLineDash([4, 4]); ctx.strokeStyle = MUTE; ctx.lineWidth = 2; ctx.strokeRect(x, yy, 90, 34); ctx.setLineDash([]); }
  }
  const mx0 = 952, my0 = 110, mh = 640, ms = mh / MH;
  ctx.fillStyle = PAPER; ctx.fillRect(mx0 - 4, my0 - 4, 44, mh + 8); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.strokeRect(mx0 - 4, my0 - 4, 44, mh + 8);
  const v0 = clamp(G.cam, 0, MH), v1 = clamp(G.cam + VH, 0, MH);
  ctx.strokeStyle = MUTE; ctx.lineWidth = 2; ctx.strokeRect(mx0 - 2, my0 + v0 * ms, 40, (v1 - v0) * ms);
  for (const u of G.units) {
    const mx = mx0 + 18 + (u.x - 500) * 0.04, my = my0 + u.y * ms;
    if (isStruct(u)) { if (!u.alive) continue; ctx.fillStyle = u.team ? INK : ACCENT; ctx.fillRect(mx - 6, my - 6, 12, 12); continue; }
    if (!visible(0, u)) continue;
    if (u.kind === 'champ') { if (u === y) drawShape(u.cls.shape, mx, my, 8, { fill: ACCENT }); else if (u.team === 0) drawShape(u.cls.shape, mx, my, 6, { stroke: ACCENT, lw: 3 }); else drawShape(u.cls.shape, mx, my, 7, { fill: INK }); }
    else disc(mx, my, 2.5, u.team ? INK : ACCENT);
  }
  if (G.phase === 'play' && canShop(y)) {
    if (!G.shopHidden) drawShop(y);
    else {
      ctx.fillStyle = ACCENT; ctx.fillRect(SHOP_BTN.x, SHOP_BTN.y, SHOP_BTN.w, SHOP_BTN.h);
      txt('SHOP', SHOP_BTN.x + SHOP_BTN.w / 2, SHOP_BTN.y + 18, 15, { weight: 600, color: PAPER });
    }
  }
  for (const key in BTN) {
    const bt = BTN[key], cdv = key === 'atk' ? 0 : y.cd[key], cdMax = key === 'atk' ? 1 : cdOf(y, key);
    const ready = cdv <= 0 && y.dead <= 0;
    disc(bt.x, bt.y, bt.r, ready ? PAPER : WASH);
    circ(bt.x, bt.y, bt.r - 3, ready ? (key === 'atk' ? ACCENT : INK) : RULE, key === 'atk' ? 7 : 5);
    if (cdv > 0) { ctx.beginPath(); ctx.moveTo(bt.x, bt.y); ctx.arc(bt.x, bt.y, bt.r - 6, -Math.PI / 2, -Math.PI / 2 + TAU * clamp(cdv / cdMax, 0, 1)); ctx.closePath(); ctx.fillStyle = RULE; ctx.fill(); txt(Math.ceil(cdv).toString(), bt.x, bt.y, 30, { weight: 600 }); }
    else txt(bt.label, bt.x, bt.y, key === 'atk' ? 30 : 34, { font: key === 'atk' ? MONO : DISPLAY, weight: 600, color: key === 'atk' ? ACCENT : INK });
    const desk = G.lastPointer === 'mouse', hint = desk ? { atk: 'F', w: 'SPACE' }[key] : null;
    if (hint) { const hy = bt.y + bt.r + 4, hw = hint.length * 11 + 14; ctx.fillStyle = INK; ctx.fillRect(bt.x - hw / 2, hy - 11, hw, 22); txt(hint, bt.x, hy, 13, { weight: 600, color: PAPER }); }
  }
  if (G.joy) { circ(G.joy.ax, G.joy.ay, 90, INK, 3, [6, 6]); disc(G.joy.ax + G.joy.dx, G.joy.ay + G.joy.dy, 36, ACCENT); }
  else if (G.t < 25 && G.phase === 'play') {
    if (G.lastPointer === 'mouse') { ctx.fillStyle = PAPER; ctx.fillRect(110, VH - 118, 280, 44); txt('WASD TO MOVE', 250, VH - 105, 18, { weight: 600 }); txt('HOLD Q TO AIM · SPACE DASH', 250, VH - 82, 13, { color: MUTE }); }
    else txt('THUMB HERE TO MOVE', 250, VH - 100, 18, { color: MUTE });
  }
  if (concedeOpen()) {
    const armed = performance.now() - G.concedeArm < 3000, b = CONCEDE_BTN;
    ctx.fillStyle = armed ? INK : PAPER; ctx.fillRect(b.x, b.y, b.w, b.h);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(b.x, b.y, b.w, b.h);
    txt(armed ? 'TAP AGAIN' : 'CONCEDE', b.x + b.w / 2, b.y + b.h / 2 + 1, 15, { weight: 600, color: armed ? PAPER : INK });
  }
  if (G.vote && performance.now() < G.vote.until && G.phase === 'play') {
    ctx.fillStyle = INK; ctx.fillRect(0, 146, 1000, 44);
    txt('CONCEDE VOTE ' + G.vote.yes + '/' + G.vote.need + ' · TAP CONCEDE TWICE TO AGREE', 500, 169, 17, { weight: 600, color: PAPER });
  }
  if (G.net && !Online.open && G.phase !== 'over') banner('RECONNECTING…', VH / 2 - 25, 40, INK);
  if (y.dead > 0 && G.phase === 'play') banner('RESPAWN ' + Math.ceil(y.dead), VH - 388, 52, INK);
  if (G.waveN === 0 && G.phase === 'play' && !canShop(y)) txt('PIPS IN ' + Math.ceil(G.waveT), 500, 160, 20, { weight: 600, color: MUTE });
  if (G.notice && G.t - G.notice.t < 4.5) {
    ctx.globalAlpha = Math.min(1, (4.5 - (G.t - G.notice.t)) * 2);
    ctx.fillStyle = INK; ctx.fillRect(0, VH - 432, 1000, 48); txt(G.notice.s, 500, VH - 407, 20, { weight: 600, color: PAPER });
    ctx.globalAlpha = 1;
  }
}
function card(x, y, w, h) { ctx.fillStyle = PAPER; ctx.fillRect(x, y, w, h); ctx.strokeStyle = INK; ctx.lineWidth = 5; ctx.strokeRect(x, y, w, h); }
function drawIdle() {
  ctx.setTransform(k, 0, 0, k, 0, menuOff() * k);
  const y0 = 250, h = 560;
  card(70, y0, 860, h);
  txt('MONOLANE', 500, y0 + 70, 66, { font: DISPLAY });
  ['DESTROY THE ENEMY CORE (TOP)', '3 CLASSES · 4 ITEM SLOTS', 'EVERY ITEM UPGRADES ONCE'].forEach((l, i) => txt(l, 500, y0 + 140 + i * 40, 22));
  for (const r of playRects()) {
    const on = r.id === playMode;
    ctx.fillStyle = on ? INK : PAPER; ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(r.x, r.y, r.w, r.h);
    txt(r.label, r.x + r.w / 2, r.y + r.h / 2 + 1, 19, { weight: 600, color: on ? PAPER : INK });
  }
  txt(playMode === 'online' ? 'PICK A MODE · BOTS FILL EMPTY SEATS' : 'PICK A MODE · YOU VS BOTS, NO INTERNET NEEDED', 500, y0 + 375, 18, { weight: 600, color: MUTE });
  txt('PLAYING AS ' + NAME.toUpperCase(), 500, y0 + h - 20, 14, { color: MUTE });
  if (IS_TEST) { ctx.fillStyle = INK; ctx.fillRect(82, 262, 140, 40); txt('TEST SITE', 152, 283, 14, { weight: 600, color: PAPER }); }
  { const b = FS_BTN, on = isFull(); ctx.fillStyle = on ? INK : PAPER; ctx.fillRect(b.x, b.y, b.w, b.h); ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(b.x, b.y, b.w, b.h);
    txt(on ? 'EXIT FULL' : 'FULL SCREEN', b.x + b.w / 2, b.y + b.h / 2 + 1, 14, { weight: 600, color: on ? PAPER : INK }); }
  if (G.toast && performance.now() - G.toast.at < 6000) { ctx.fillStyle = INK; ctx.fillRect(70, y0 - 70, 860, 50); txt(G.toast.s.toUpperCase(), 500, y0 - 44, 18, { weight: 600, color: PAPER }); }
  for (const r of modeRects()) {
    ctx.fillStyle = r.n === lastMode ? ACCENT : PAPER; ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = ACCENT; ctx.lineWidth = 5; ctx.strokeRect(r.x, r.y, r.w, r.h);
    txt(r.n + 'V' + r.n, r.x + r.w / 2, r.y + r.h / 2, 46, { font: DISPLAY, color: r.n === lastMode ? PAPER : ACCENT });
  }
}
function drawPick() {
  ctx.setTransform(k, 0, 0, k, 0, menuOff() * k);
  card(60, 150, 880, 900);
  txt('PICK YOUR CLASS', 500, 205, 44, { font: DISPLAY });
  txt((playMode === 'online' ? 'ONLINE · ' : 'PRACTICE · ') + (G.pickMode === 1 ? '1V1 PICKS ARE BLIND' : G.pickMode + 'V' + G.pickMode + ' · BOTS FILL EMPTY SEATS'), 500, 256, 18, { weight: 600, color: MUTE });
  for (const r of classRects()) {
    const K = CLASSES[r.key], last = r.key === lastCls;
    ctx.fillStyle = PAPER; ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = last ? ACCENT : INK; ctx.lineWidth = last ? 7 : 4; ctx.strokeRect(r.x + 2, r.y + 2, r.w - 4, r.h - 4);
    drawShape(K.shape, r.x + 92, r.y + 100, 44, { fill: ACCENT });
    txt(K.label, r.x + 180, r.y + 50, 42, { font: DISPLAY, align: 'left' });
    txt(K.role, r.x + r.w - 26, r.y + 50, 17, { weight: 600, color: MUTE, align: 'right' });
    txt('Q ' + K.q + ' · ' + K.qTxt, r.x + 180, r.y + 104, 18, { align: 'left' });
    txt('E ' + K.e + ' · ' + K.eTxt, r.x + 180, r.y + 138, 18, { align: 'left' });
    txt(K.stat, r.x + 180, r.y + 172, 16, { color: MUTE, align: 'left' });
  }
  ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(BACK_BTN.x, BACK_BTN.y, BACK_BTN.w, BACK_BTN.h);
  txt('← MODES', BACK_BTN.x + BACK_BTN.w / 2, BACK_BTN.y + BACK_BTN.h / 2 + 1, 19, { weight: 600 });
  txt('TAP A CLASS TO LOCK IN', 680, BACK_BTN.y + BACK_BTN.h / 2 + 1, 18, { color: MUTE });
}
function drawReveal() {
  ctx.setTransform(k, 0, 0, k, 0, menuOff() * k);
  card(60, 230, 880, 780);
  txt(G.mode === 1 ? '1V1 · BLIND PICK' : G.mode + 'V' + G.mode, 500, 295, 44, { font: DISPLAY });
  const row = (team, y0, label) => {
    txt(label, 500, y0, 18, { weight: 600, color: MUTE });
    const list = G.champs.filter(c => c.team === team), n = list.length;
    list.forEach((c, i) => {
      const x = 500 + (i - (n - 1) / 2) * 260, col = team ? INK : ACCENT;
      if (team === 0 && c !== G.me) drawShape(c.cls.shape, x, y0 + 90, 32, { fill: PAPER, stroke: ACCENT, lw: 8 });
      else drawShape(c.cls.shape, x, y0 + 90, 36, { fill: col });
      txt(champName(c), x, y0 + 162, 20, { weight: 600, color: col });
      txt(c.cls.label, x, y0 + 194, 18, { color: MUTE });
    });
  };
  row(0, 365, 'YOUR TEAM');
  txt('VS', 500, 625, 34, { font: DISPLAY, color: MUTE });
  row(1, 680, G.mode === 1 ? 'OPPONENT' : 'ENEMY TEAM');
  if (G.connecting || G.waiting) { txt(G.connecting ? 'CONNECTING…' : 'WAITING FOR PLAYERS TO CONNECT', 500, 960, 20, { weight: 600, color: MUTE }); return; }
  const full = G.net ? 3 : 2.4;
  ctx.fillStyle = RULE; ctx.fillRect(160, 955, 680, 10); ctx.fillStyle = ACCENT; ctx.fillRect(160, 955, 680 * clamp(1 - G.revealT / full, 0, 1), 10);
}
function drawScoreboard() {
  ctx.setTransform(k, 0, 0, k, 0, 0);
  ctx.fillStyle = PAPER; ctx.globalAlpha = 0.96; ctx.fillRect(0, 95, 1000, VH - 95); ctx.globalAlpha = 1;
  const win = G.over === 0;
  txt(win ? 'VICTORY' : 'DEFEAT', 500, 190, 88, { font: DISPLAY, color: win ? ACCENT : INK });
  const why = G.endReason === 'concede' ? (win ? ' · THEY CONCEDED' : ' · CONCEDED') : '';
  txt(G.mode + 'V' + G.mode + ' · ' + fmtT(G.t) + ' · KILLS ' + G.kills[0] + '–' + G.kills[1] + why, 500, 270, 24, { weight: 600 });
  const X = { name: 92, lv: 330, kda: 440, cs: 558, dmg: 658, build: 728 };
  let yy = 340;
  [['LV', X.lv], ['K/D/A', X.kda], ['PIPS', X.cs], ['DMG', X.dmg]].forEach(([h, x]) => txt(h, x, yy, 17, { color: MUTE, weight: 600 }));
  txt('BUILD', X.build, yy, 17, { color: MUTE, weight: 600, align: 'left' });
  seg(40, yy + 22, 960, yy + 22, INK, 3);
  yy += 64;
  for (const team of [0, 1]) {
    for (const c of G.champs.filter(ch => ch.team === team)) {
      const col = team ? INK : ACCENT, s = c.st;
      if (c === G.me) { ctx.fillStyle = WASH; ctx.fillRect(40, yy - 32, 920, 64); }
      drawShape(c.cls.shape, 66, yy, 13, team === 0 && c !== G.me ? { fill: PAPER, stroke: ACCENT, lw: 4 } : { fill: col });
      const nm = c === G.me ? 'YOU' : c.human && c.name ? c.name.toUpperCase().slice(0, 11) : champName(c);
      txt(nm, X.name, c.left ? yy - 9 : yy, nm.length > 8 ? 18 : 23, { weight: 600, color: col, align: 'left' });
      if (c.left) txt('LEFT THE MATCH', X.name, yy + 14, 12, { weight: 600, color: MUTE, align: 'left' });
      txt(String(c.lvl), X.lv, yy, 23);
      txt(s.k + '/' + s.d + '/' + s.a, X.kda, yy, 23, { weight: 600 });
      txt(String(s.cs), X.cs, yy, 23);
      txt(fmtK(s.dmgChamp), X.dmg, yy, 23);
      for (let j = 0; j < SLOTS; j++) {
        const e = c.items[j], bx = X.build + j * 56, by = yy - 22;
        if (!e) { ctx.setLineDash([4, 4]); ctx.strokeStyle = RULE; ctx.lineWidth = 2; ctx.strokeRect(bx, by, 48, 44); ctx.setLineDash([]); continue; }
        if (e.tier === 2) { ctx.fillStyle = col; ctx.fillRect(bx, by, 48, 44); }
        else { ctx.strokeStyle = col; ctx.lineWidth = 3; ctx.strokeRect(bx + 1.5, by + 1.5, 45, 41); }
        txt(ITEM[e.key].code, bx + 24, yy + 1, 16, { weight: 600, color: e.tier === 2 ? PAPER : col });
      }
      yy += 72;
    }
    if (team === 0) { seg(40, yy - 30, 960, yy - 30, RULE, 2); yy += 14; }
  }
  txt(G.practice ? 'PRACTICE MATCH · NOT RANKED' : 'ACCOUNTS AND SAVED STATS ARE COMING NEXT', 500, yy + 24, 18, { weight: 600, color: MUTE });
  txt('FILLED BOX = UPGRADED TO II', 500, yy + 62, 16, { color: MUTE });
  if (G.endT > 1) txt('TAP TO CONTINUE', 500, yy + 118, 26, { weight: 600, color: ACCENT });
}
function drawQueue() {
  ctx.setTransform(k, 0, 0, k, 0, menuOff() * k);
  card(70, 300, 860, 560);
  const q = MM.info || { mode: lastMode, at: performance.now(), n: 1, need: 2, fillIn: 8000, recvAt: performance.now() };
  const secs = Math.floor((performance.now() - q.at) / 1000), fill = Math.max(0, Math.ceil((q.fillIn - (performance.now() - q.recvAt)) / 1000));
  txt('FINDING PLAYERS', 500, 380, 50, { font: DISPLAY });
  txt(q.mode + 'V' + q.mode + ' · ' + CLASSES[lastCls].label + ' · ' + fmtT(secs), 500, 450, 24, { weight: 600 });
  txt(q.n + ' OF ' + q.need + ' PLAYERS IN QUEUE', 500, 530, 22, {});
  txt(q.err ? q.err.toUpperCase() : fill > 0 ? 'BOTS FILL EMPTY SEATS IN ' + fill + ' S' : 'STARTING…', 500, 580, 20, { weight: 600, color: q.err ? INK : ACCENT });
  const dots = '•'.repeat(1 + Math.floor(performance.now() / 400) % 3);
  txt(dots, 500, 660, 40, { color: ACCENT });
  ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(BACK_BTN.x, BACK_BTN.y, BACK_BTN.w, BACK_BTN.h);
  txt('CANCEL', BACK_BTN.x + BACK_BTN.w / 2, BACK_BTN.y + BACK_BTN.h / 2 + 1, 19, { weight: 600 });
}
function render() {
  const c = G.me;
  if (!c) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.fillStyle = PAPER; ctx.fillRect(0, 0, cv.width, cv.height); ctx.setTransform(k, 0, 0, k, 0, 0); txt('CONNECTING…', 500, VH / 2, 26, { weight: 600, color: MUTE }); return; }
  const shopNow = G.phase === 'play' && canShop(c);
  if (shopNow && !G.wasShop) { G.shopMode = 'buy'; G.shopHidden = false; }
  G.wasShop = shopNow;
  const target = alive(c) ? c.y - VH * 0.55 : WELL[0].y - VH * 0.6;
  G.cam += (clamp(target, -60, MH - VH + 280) - G.cam) * 0.15;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1;
  drawWorld();
  drawFog();
  drawUnits();
  drawHUD();
  if (G.phase === 'idle') drawIdle();
  else if (G.phase === 'pick') drawPick();
  else if (G.phase === 'queue') drawQueue();
  else if (G.phase === 'reveal') drawReveal();
  else if (G.phase === 'over') drawScoreboard();
}
/* ---------- loop ---------- */
let last = performance.now(), acc = 0, lastVec = '', lastVecAt = 0;
// send the stick / WASD direction only when it changes (and at most 20 times a second)
function pushMove(now) {
  if (G.phase !== 'play' || !G.me || G.menu) { lastVec = ''; return; }
  const j = G.joy && hyp(G.joy.dx, G.joy.dy) > 14 ? G.joy : keyVec();
  const v = j ? [Math.round(j.dx / 6) * 6, Math.round(j.dy / 6) * 6] : null;
  const key = v ? v.join(',') : '';
  if (key === lastVec || (v && now - lastVecAt < 50)) return;
  lastVec = key; lastVecAt = now;
  send(v ? { t: 'mv', dx: v[0], dy: v[1] } : { t: 'mv', dx: 0, dy: 0 });
}
function frame(now) {
  const dt = Math.min(0.1, (now - last) / 1000); last = now;
  if (!document.hidden && G) {
    if (G.phase === 'reveal') {
      if (!G.net) { G.revealT -= dt; if (G.revealT <= 0) { G.phase = 'play'; acc = 0; } }
      else if (!G.waiting && !G.connecting) G.revealT = Math.max(0, G.revealT - dt);
    } else if (G.phase === 'play' && G.practice) {
      acc += dt; let n = 0;
      while (acc >= DT && n < 8 && G.phase === 'play') { PE.step(); acc -= DT; n++; }
      if (n === 8) acc = 0;
    } else if (G.phase === 'over') G.endT += dt;
    Online.tick(now);
    pushMove(now);
    for (const f of G.fx) f.age += dt; G.fx = G.fx.filter(f => f.age < f.life);
    for (const t of G.texts) t.age += dt; G.texts = G.texts.filter(t => t.age < 0.9);
    render();
    if (mouseScr && G.lastPointer === 'mouse') updateCursor(mouseScr);
  }
  requestAnimationFrame(frame);
}
// phones pause background tabs and drop sockets: reconnect the moment the game is visible again
document.addEventListener('visibilitychange', () => {
  if (document.hidden) return;
  if (Online.ws && !Online.open) Online.ws.reconnect();
  if (MM.ws && MM.ws.readyState !== 1) MM.ws.reconnect();
});
// test hook for automated browser checks (only with ?debug in the address)
if (/[?&]debug\b/.test(location.search)) window.__ml = { get G() { return G; }, PE, Online, MM };
// came back to the page mid-match? go straight back in
const resume = load('ml.match');
toMenu();
fit();
if (resume && resume.id && Date.now() - resume.at < 40 * 60 * 1000) Online.join(resume.id);
requestAnimationFrame(frame);
if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (G) render(); });
